import { Max, Min } from "@pdfking/math"
import {
  CreateMixin,
  CreateRenderFunction,
  NextFrame,
} from "@pdfking/reactivity/preact"
import { contains, first, last, length, map, object_map } from "@pdfking/rfunc"
import {
  IsString,
  Items,
  KeyDrop,
  Keys,
  RecordMerge,
  Values,
} from "@pdfking/tfunc"
import { RootMixin } from "@pdfking/workflow/mixins/RootMixin"
import fire_event from "@pdfking/workflow/utils/fire_event"
import keyboard from "../constants/keyboard"
import can_hover from "../features/can_hover"
import { SearchMixin } from "../mixins/Search"
import cx from "../utils/cx"
import strip_tags from "../utils/strip_tags"
import truncate from "../utils/truncate"

export interface SelectionInfo {
  id: string
  unicode: string
  group?: string
  score?: number
}

export const SearchselectMixin = CreateMixin({
  computed: {
    active_key() {
      return Keys(this.results)[this.active]
    },
    all_options(): Record<string, any> {
      return this.options || {}
    },
    default_results(): Record<string, any> | any[] | null {
      if (this.show_all) {
        return this.all_options
      }

      if (this.multiple) {
        return this.selection
      }

      return null
    },

    input_placeholder() {
      const res = strip_tags(
        map((s: SelectionInfo) => s.unicode, Values(this.selection)).join(", ")
      )

      if (length(this.selection) > 1) {
        return `${length(this.selection)} elementi: ${res}`
      }

      return res
    },
  },
  data: {
    active: -1,
    open: false,
    query: "",
    selection: {} as Record<string, any>,
    show_all: true,
  },
  init() {
    this.selection = this.options
      ? object_map(
          (v: string | SelectionInfo) => (IsString(v) ? v : v.id),
          (v: string | SelectionInfo) => (IsString(v) ? this.options[v] : v),
          this.value
        )
      : {}
  },
  methods: {
    contains,
    first,
    keydown(e: KeyboardEvent) {
      this.show_all = true

      switch (e.keyCode) {
        case keyboard.BACKSPACE:
          if (!this.query) {
            this.pop()
          }
          break
        case keyboard.ESCAPE:
          this.open = false
          this.query = ""
          break
        case keyboard.ARROW_DOWN:
          this.active = Min(
            length(this.results) - 1,
            this.active + (this.open ? 1 : 0)
          )
          this.open = true

          e.preventDefault()
          break
        case keyboard.ARROW_UP:
          this.active = Min(
            Max(-1, this.active - (this.open ? 1 : 0)),
            length(this.results) - 1
          )
          this.open = this.active >= 0

          this.query = this.open ? this.query : ""

          e.preventDefault()

          break
        case keyboard.ENTER:
          if (this.open && this.active >= 0) {
            this.toggle(Values(this.results)[this.active] as SelectionInfo)
          }
          e.preventDefault()
          break
        default:
          this.open = true

          this.query = (
            this.root?.querySelector("input.form-control") as HTMLInputElement
          ).value

          break
      }
    },
    length,
    pop() {
      if (length(this.selection) > 0) {
        this.selection = KeyDrop(this.selection, [last(Keys(this.selection))])
      }
    },
    toggle(info: SelectionInfo) {
      if (this.multiple) {
        if (contains(this.selection, info.id)) {
          this.selection = KeyDrop(this.selection, [info.id])
        } else {
          this.selection = RecordMerge(this.selection, { [info.id]: info })
        }
      } else {
        this.open = false
        this.query = ""
        this.selection = { [info.id]: info }
      }

      if (can_hover())
        (
          this.root?.querySelector("input.form-control") as HTMLInputElement
        ).focus()
    },
    truncate,
    Values,
  },
  mixins: [SearchMixin, RootMixin],
  props: {
    class: "",
    disabled: false,
    hide_dropdown: false,
    multiple: false,
    name: "",
    options: {} as Record<string, any>,
    value: null as string | string[] | null,
  },

  render() {
    return (
      <div
        class={`input-autocomplete ${this.class}`}
        onClick={(e: Event) => e.preventDefault()}
      >
        <div class={cx({ "input-group": !this.hide_dropdown })}>
          <input
            class="form-control"
            onBlur={() => {
              this.open = false
              this.query = ""
            }}
            onClick={() => {
              this.open = true
              this.show_all = false
            }}
            onInput={(e: Event) => {
              this.query = (e.target as HTMLInputElement).value
            }}
            onKeyDown={this.keydown}
            placeholder={this.input_placeholder}
            value={this.query}
          />

          {Items(this.selection).map(([id, _info]: [string, any]) => (
            <input key={id} name={this.name} type="hidden" value={id} />
          ))}

          {!this.hide_dropdown && (
            <select
              class="form-select flex-grow-0"
              onClick={(e: Event) => {
                e.stopPropagation()
                e.preventDefault()
                this.open = !this.open || !this.show_all
                this.query = ""
                this.show_all = true
              }}
              onMouseDown={(e: Event) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              onMouseUp={(e: Event) => {
                e.stopPropagation()
                e.preventDefault()
              }}
            ></select>
          )}
        </div>
        {this.open && length(this.results) > 0 && (
          <div class="autocomplete-menu no-scrollbar">
            <ul class="dropdown-list dropdown-menu d-flex flex-column">
              {Values(this.results).map((info: any, i: number) => (
                <li
                  class={cx("dropdown-item onhover", {
                    active: info.id == this.active_key,
                    selected: contains(this.selection, info.id),
                  })}
                  key={i}
                  onClick={(e: Event) => {
                    e.stopPropagation()
                    e.preventDefault()
                    this.toggle(info)
                  }}
                  onMouseDown={(e: Event) => {
                    e.stopPropagation()
                    e.preventDefault()
                  }}
                  onMouseUp={(e: Event) => {
                    e.stopPropagation()
                    e.preventDefault()
                  }}
                  style={{ order: info.score || 0 }}
                >
                  <a class="d-flex justify-content-between align-items-center">
                    <span dangerouslySetInnerHTML={{ __html: info.unicode }} />
                    <small
                      class={cx({
                        "onhover-show-visibility": info.id != this.active_key,
                      })}
                    >
                      {info.group}
                    </small>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  },
  watch: {
    selection() {
      NextFrame(() =>
        fire_event(
          this.root?.querySelector("input[type=hidden], select.form-select"),
          "change"
        )
      )
    },
  },
})

export const Searchselect = CreateRenderFunction(SearchselectMixin)
